<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import RealEstateBrokerPaymentMethodCard from '../components/RealEstateBrokerPaymentMethodCard.vue';
import RealEstateBrokerPaymentMethodDialog from '../components/RealEstateBrokerPaymentMethodDialog.vue';
import RealEstateBrokerPaymentMethodSuccessDialog from '../components/RealEstateBrokerPaymentMethodSuccessDialog.vue';
import RealEstateBrokerPaymentIdentity from '../components/RealEstateBrokerPaymentIdentity.vue';
import { useRealEstateBrokerCommissionDetail } from '../composables/useRealEstateBrokerCommissionDetail';
import { useRealEstateBrokerPaymentMethods } from '../composables/useRealEstateBrokerPaymentMethods';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const installmentId = computed(() => route.params.installmentId);
const detailState = useRealEstateBrokerCommissionDetail({
  installmentId,
  notify: useAlert,
  t,
});
const paymentState = useRealEstateBrokerPaymentMethods({
  installmentId,
  notify: useAlert,
  t,
});
const dialogVisible = ref(false);
const editingMethod = ref(null);
const successDialogVisible = ref(false);

const goBack = () =>
  router.push({
    name: 'operacao_imobiliaria_corretor_parcela',
    params: {
      accountId: route.params.accountId,
      installmentId: installmentId.value,
    },
  });
const saveMethod = async payload => {
  if (await paymentState.save(payload, editingMethod.value?.id)) {
    dialogVisible.value = false;
    editingMethod.value = null;
  }
};
const openNewMethod = () => {
  editingMethod.value = null;
  dialogVisible.value = true;
};
const editMethod = method => {
  editingMethod.value = method;
  dialogVisible.value = true;
};
const selectMethod = async () => {
  if (await paymentState.select({ notifySuccess: false })) {
    successDialogVisible.value = true;
  }
};
const closeSuccess = () => {
  successDialogVisible.value = false;
  goBack();
};
</script>

<template>
  <div class="min-h-full bg-lais-bg-soft">
    <LaisHeader
      :title="t('REAL_ESTATE_BROKER_COMMISSION.PAYMENT_TITLE')"
      :subtitle="t('REAL_ESTATE_BROKER_COMMISSION.PAYMENT_SUBTITLE')"
      :breadcrumb="[
        { label: t('REAL_ESTATE_BROKER_DASHBOARD.TITLE') },
        { label: t('REAL_ESTATE_BROKER_COMMISSION.PAYMENT_TITLE') },
      ]"
      show-back
      :back-label="t('REAL_ESTATE_BROKER_COMMISSION.ACTIONS.BACK')"
      @back="goBack"
    />
    <main
      class="mx-auto grid w-full max-w-[1100px] gap-5 px-4 pb-8 sm:px-6 lg:grid-cols-[0.72fr_1.28fr] lg:px-10"
    >
      <RealEstateBrokerPaymentIdentity :detail="detailState.detail.value" />
      <LaisCard
        :title="t('REAL_ESTATE_BROKER_COMMISSION.PAYMENT.METHODS')"
        density="tight"
      >
        <div class="space-y-4 p-4">
          <p
            v-if="paymentState.loading.value"
            class="text-sm text-lais-text-muted"
          >
            {{ t('REAL_ESTATE_BROKER_DASHBOARD.TABLE.LOADING') }}
          </p>
          <p
            v-else-if="!paymentState.methods.value.length"
            class="text-sm text-lais-text-muted"
          >
            {{ t('REAL_ESTATE_BROKER_COMMISSION.PAYMENT.EMPTY') }}
          </p>
          <RealEstateBrokerPaymentMethodCard
            v-for="method in paymentState.methods.value"
            :key="method.id"
            :method="method"
            :selected="
              String(paymentState.selectedId.value) === String(method.id)
            "
            @edit="editMethod"
            @select="paymentState.selectedId.value = $event"
          />
          <p
            v-if="paymentState.error.value"
            class="rounded-lais border border-red-200 bg-red-50 p-3 text-sm text-red-700"
          >
            {{ paymentState.error.value }}
          </p>
          <p
            v-if="
              detailState.detail.value &&
              !detailState.detail.value.acoes?.pode_selecionar_metodo
            "
            class="rounded-lais border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800"
          >
            {{ t('REAL_ESTATE_BROKER_COMMISSION.PAYMENT.NOT_AVAILABLE') }}
          </p>
          <div class="flex flex-wrap justify-end gap-3">
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-plus"
              @click="openNewMethod"
            >
              {{ t('REAL_ESTATE_BROKER_COMMISSION.ACTIONS.ADD_METHOD') }}
            </LaisPrimaryButton>
            <LaisPrimaryButton
              :disabled="
                !paymentState.canSelect.value ||
                !detailState.detail.value?.acoes?.pode_selecionar_metodo
              "
              :is-loading="paymentState.saving.value"
              icon="i-lucide-check"
              @click="selectMethod"
            >
              {{ t('REAL_ESTATE_BROKER_COMMISSION.ACTIONS.SELECT') }}
            </LaisPrimaryButton>
          </div>
        </div>
      </LaisCard>
    </main>
    <RealEstateBrokerPaymentMethodDialog
      :show="dialogVisible"
      :catalog="paymentState.catalog.value"
      :method="editingMethod"
      :saving="paymentState.saving.value"
      @close="
        dialogVisible = false;
        editingMethod = null;
      "
      @save="saveMethod"
    />
    <RealEstateBrokerPaymentMethodSuccessDialog
      :show="successDialogVisible"
      @close="closeSuccess"
    />
  </div>
</template>
